import React, { useEffect, useRef } from "react";
import { useState } from "react";
import "../Login/login.css";
import HttpCommon from "../../Utils/http-common";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import swal from "sweetalert";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import FormGroup from "../Common/FormGroup";
import InputField from "../Common/InputField";
import PasswordInput from "../Common/FormGroup/PasswordInput";
import {
  LOGIN_TYPE,
  LOGIN_USER_ROLE_KEY,
  TOKEN_KEY,
  USERID_KEY,
  USER_ROLE_KEY,
} from "../../Frontend/Constant";
import ApiService from "../../Frontend/Api/ApiServices";

const defaultValue = {
  username: "",
  password: "",
};

const emailOnlySchema = Yup.object({
  username: Yup.string().email(`Invalid User format`).required("User is required"),
});

const passwordLoginSchema = Yup.object({
  username: Yup.string().email(`Invalid User format`).required("User is required"),
  password: Yup.string().required("Invalid Password"),
});

async function completeLogin(res) {
  const user_id = res.data.user?.id;
  const token = res.data.token;
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USERID_KEY, user_id);
  localStorage.setItem(LOGIN_TYPE, "user_login");
  if (res?.data?.user?.user_role !== "Administrator") {
    window.location.href = "/user-information";
    return;
  }
  const { data } = await ApiService.getMembersByUserId(user_id);
  for (var i = 0; i < data.members.length; i++) {
    if (data.members[i].relation === "Self") {
      const { data: userInfo } = await ApiService.getSelfInfo(
        data?.members?.[i]?.user_id
      );
      localStorage.setItem(USER_ROLE_KEY, userInfo?.user_role);
      localStorage.setItem(LOGIN_USER_ROLE_KEY, userInfo?.user_role);
      window.location.href = "/user-information";
      return;
    }
  }
  window.location.href = "/myFootprintID";
}

function Login() {
  const formRef = useRef();
  const [check, setCheck] = useState(false);
  const navigate = useNavigate();

  const [initialValue, setInitialValue] = useState(defaultValue);
  const [emailStatus, setEmailStatus] = useState("idle"); // idle|checking|whitelist|password|not_found
  const [sendingLink, setSendingLink] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("password") !== null) {
      setInitialValue({
        username: localStorage.getItem("username"),
        password: localStorage.getItem("password"),
      });
      setEmailStatus("password");
    }
    setCheck(localStorage.getItem("checkbox") || false);
  }, []);

  const checkEmail = async (email, { setErrors } = {}) => {
    const trimmed = String(email || "").trim();
    if (!trimmed) return;
    setEmailStatus("checking");
    try {
      const res = await HttpCommon.post("/login-email-check", {
        email: trimmed,
        username: trimmed,
      });
      if (res?.data?.err === "INVALID_DOMAIN") {
        swal("Invalid Domain!", "Invalid domain for login!", "error");
        setEmailStatus("idle");
        return;
      }
      const status = res?.data?.status || "not_found";
      setEmailStatus(
        status === "whitelist"
          ? "whitelist"
          : status === "password"
            ? "password"
            : "not_found"
      );
      setErrors?.({});
    } catch (err) {
      setEmailStatus("idle");
      if (err.code === "ERR_NETWORK") {
        swal("Network Error!", "Failed to connect server.", "error");
      } else {
        swal("Error!", err?.response?.data?.message || "Something went wrong", "error");
      }
    }
  };

  const handleSendLink = async (email) => {
    setSendingLink(true);
    try {
      const res = await HttpCommon.post("/whitelist-send-link", {
        email,
        username: email,
      });
      if (res?.data?.success === 1) {
        swal("Check your email", res?.data?.message || "Link sent.", "success");
      } else if (res?.data?.err === "INVALID_DOMAIN") {
        swal("Invalid Domain!", "Invalid domain for login!", "error");
      } else {
        swal("Unable to send link", res?.data?.message || "Please try again.", "error");
      }
    } catch (err) {
      swal("Error!", err?.response?.data?.message || "Something went wrong", "error");
    } finally {
      setSendingLink(false);
    }
  };

  const handleSubmit = async (Values, { setFieldError, setErrors, setSubmitting }) => {
    try {
      if (emailStatus === "idle" || emailStatus === "checking") {
        await checkEmail(Values.username, { setErrors });
        return;
      }
      if (emailStatus === "whitelist") {
        await handleSendLink(Values.username);
        return;
      }

      const res = await HttpCommon.post("/login", Values);
      if (res.data.isAuthenticated) {
        await completeLogin(res);
      } else {
        if (
          res.data.err ===
          "Too many failed attempts. Try again after 30 minutes."
        ) {
          setFieldError("password", res.data.err);
        } else if (res.data.err === "L_LOGIN__INCORRECT_CREDENTIALS") {
          setFieldError("password", "Invalid login credential");
        } else if (res.data.err === "L_LOGIN__NOT_ACTIVATED") {
          setFieldError("password", "Please activate your account");
        } else if (res.data.err === "L_LOGIN__RESTRICTED_USER") {
          setFieldError(
            "password",
            "Dependent user cannot login via standard login method"
          );
        } else if (res.data.err === "INVALID_DOMAIN") {
          swal("Invalid Domain!", "Invalid domain for login!", "error");
        } else if (res.data.err === "L_LOGIN__INCORRECT_MEMBERSHIP_NO_CREDENTIALS") {
          setFieldError("password", "Invalid login credential");
        }
      }
    } catch (err) {
      console.log("error: ", err);
      if (err.code === "ERR_NETWORK") {
        swal("Network Error!", "Failed to connect server.", "error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleMember = () => {
    navigate("/register");
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const showPasswordStep =
    emailStatus === "password" || emailStatus === "not_found";

  const validationSchema = showPasswordStep
    ? passwordLoginSchema
    : emailOnlySchema;

  const submitLabel = showPasswordStep
    ? "Get started"
    : emailStatus === "whitelist"
      ? sendingLink
        ? "Sending…"
        : "Send link"
      : emailStatus === "checking"
        ? "Checking…"
        : "Continue";

  return (
    <>
      <div className="main">
        <div className="container">
          <div className="wel-come-title">Welcome back</div>
          <Formik
            initialValues={initialValue}
            enableReinitialize
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
            innerRef={formRef}
          >
            {({ getFieldProps, values, isSubmitting, setFieldValue, setErrors }) => (
              <Form>
                <h1>Log In to your account</h1>
                <FormGroup
                  label="Username"
                  name="username"
                  className="loginField"
                  requiredField
                >
                  <InputField
                    type="text"
                    name="username"
                    className="textarea"
                    {...getFieldProps("username")}
                    onBlur={async (e) => {
                      getFieldProps("username").onBlur(e);
                      if (values.username && emailStatus === "idle") {
                        await checkEmail(values.username, { setErrors });
                      }
                    }}
                    onChange={(e) => {
                      setFieldValue("username", e.target.value);
                      if (emailStatus !== "idle" && emailStatus !== "checking") {
                        setEmailStatus("idle");
                        setFieldValue("password", "");
                      }
                    }}
                  />
                </FormGroup>

                {emailStatus === "whitelist" && (
                  <WhitelistHelp role="status">
                    <WhitelistHelpIcon aria-hidden="true">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <rect
                          x="3"
                          y="5"
                          width="18"
                          height="14"
                          rx="3"
                          stroke="currentColor"
                          strokeWidth="1.75"
                        />
                        <path
                          d="M4 7l8 6 8-6"
                          stroke="currentColor"
                          strokeWidth="1.75"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </WhitelistHelpIcon>
                    <WhitelistHelpCopy>
                      <span>We&apos;ll email you a secure sign-in link.</span>
                      <small>
                        Open the link to log in, then set up your password in the app.
                      </small>
                    </WhitelistHelpCopy>
                  </WhitelistHelp>
                )}

                {showPasswordStep && (
                  <FormGroup
                    label="Password"
                    name="password"
                    className="loginField"
                    svgPath={require("../../Assets/Images/lock.png")}
                    requiredField
                  >
                    <PasswordInput {...getFieldProps("password")} />
                  </FormGroup>
                )}

                {emailStatus === "not_found" && (
                  <LoginHelp role="status">
                    <span>No account found for this email</span>
                    <small>
                      Double-check the address, then enter your password if you already
                      have an account — or create one below.
                    </small>
                  </LoginHelp>
                )}

                {showPasswordStep && (
                  <div className="middle">
                    <div className="left">
                      <input
                        type="checkbox"
                        id="check"
                        onChange={(e) => {
                          setCheck(e.target.checked);
                        }}
                        checked={check}
                        style={{ marginRight: "5px" }}
                      />
                      <label htmlFor="check"> Remember Me</label>
                    </div>
                    <div className="right">
                      <label>
                        <a href="/forget-password">Forgot password?</a>
                      </label>
                    </div>
                  </div>
                )}

                {isSubmitting || sendingLink || emailStatus === "checking" ? (
                  <button
                    className="btn1"
                    style={{ display: "grid", paddingTop: "14px", paddingBottom: "14px" }}
                    type="submit"
                    disabled
                  >
                    <Loader />
                  </button>
                ) : (
                  <button className="btn1" type="submit">
                    {submitLabel}
                  </button>
                )}

                <button className="btn12 signup-btn" onClick={handleMember} type="button">
                  <img
                    src={require("../../Assets/Images/email-border.png")}
                    alt=""
                  />
                  <div className="email-btn"> Signup with email</div>
                </button>
                <a id="l4" href="/contactus" className="needhelp">
                  Need Help ?
                </a>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </>
  );
}

export default Login;

const WhitelistHelp = styled.div`
  width: 100%;
  margin: 6px 0 16px;
  display: grid;
  grid-template-columns: 36px 1fr;
  column-gap: 10px;
  align-items: start;
  box-sizing: border-box;
`;

const WhitelistHelpIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(6, 71, 108, 1);
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(36, 159, 218, 0.4);
  box-sizing: border-box;
`;

const WhitelistHelpCopy = styled.div`
  min-width: 0;
  text-align: left;
  padding-top: 1px;

  span {
    display: block;
    color: rgba(6, 71, 108, 1);
    font-size: 13.5px;
    font-weight: 600;
    line-height: 1.35;
  }

  small {
    display: block;
    margin-top: 3px;
    color: #35586b;
    font-size: 12px;
    line-height: 1.4;
  }
`;

const LoginHelp = styled.div`
  width: 100%;
  margin: 4px 0 10px;
  text-align: left;
  box-sizing: border-box;

  span {
    display: block;
    color: rgba(6, 71, 108, 1);
    font-size: 13.5px;
    font-weight: 600;
    line-height: 1.35;
  }

  small {
    display: block;
    margin-top: 3px;
    color: #35586b;
    font-size: 12px;
    line-height: 1.4;
  }
`;

const Loader = styled.div`
  justify-self: center;
  border: 5px solid #f3f3f3;
  border-radius: 50%;
  border-top: 5px solid #3498db;
  width: 12px;
  height: 12px;
  -webkit-animation: spin 2s linear infinite; /* Safari */
  animation: spin 2s linear infinite;

  /* Safari */
  @-webkit-keyframes spin {
    0% {
      -webkit-transform: rotate(0deg);
    }
    100% {
      -webkit-transform: rotate(360deg);
    }
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;
